import React from "react";
import eq from "fast-deep-equal";
import {
	Content,
	DataComponent,
	DataObserveKey,
	VisualComponent,
	FlexAlignment,
	TrueFalsy,
	SrOnly,
	Spinner,
	Paragraph,
	useStaticMode,
	useTranslations,
	useUniqueId,
	cn,
} from "@siteimprove/fancylib";
import { useLabTranslations } from "../../../translations/translations";
import { logError } from "../../../utils/error-utils";
import { BigSmall } from "../../text/big-small/big-small";
import { CsvColumnConfig } from "../../../utils/csv-exporter";
import { EmptyState } from "../../visuals/empty-state/empty-state";
import { TableExpander } from "./table-expander";
import * as scss from "./base-table.scss";

export type BaseTableProps<T> = {
	/** Column configurations */
	columns: BaseTableColumnProps<T>[];
	/** Items to be displayed */
	items: T[];
	/** Callback for when expander is clicked */
	onExpandClick?: (event: React.MouseEvent, cellPosition: BaseTableCellPosition) => void;
	/** Renderer for content of expanded cell */
	expandedContentRenderer?: (item: T, cellPosition: BaseTableCellPosition) => React.ReactNode;
	/** Position of the expanded cell */
	expandedCellPosition?: BaseTableCellPosition;
	/** Footer displayed below table data */
	footer?: React.ReactNode;
	/** Is the content top aligned? */
	topAligned?: boolean;
	/** Compare function for highlight row */
	highlightRow?: (item: T) => boolean;
	/** Function to generate a key for rows */
	rowKey?: (item: T) => string | number;
	/** Caption for the table */
	caption?: string;
	/** Does this table not have a key column? Be sure this is the case otherwise the table is less accessible */
	withoutKeyColumn?: boolean;
	/** ClassName for th elements */
	thClassName?: string;
	/** ClassName for tr elements */
	trClassName?: string;
	/** ClassName for td elements */
	tdClassName?: string;
	/** Is the table in a loading state? */
	loading?: boolean;
	/** Optional text to be displayed when the table is loading */
	loadingText?: string;
	/** Content to be shown when there's no data. An Empty State component is shown by default, but it can be overridden by another type with custom text. */
	noDataState?: React.ReactNode;
} & DataComponent &
	DataObserveKey &
	VisualComponent;

export interface BaseTableColumnProps<T> {
	/** Header of the column */
	header: React.ReactNode;
	/** Renderer for column content */
	render: (item: T, cellPosition: BaseTableCellPosition) => React.ReactNode;
	/** Are cells in this column expandable? */
	isExpandable?: (item: T, cellPosition: BaseTableCellPosition) => TrueFalsy;
	/** Width of the column */
	width?: string | number;
	/** Padding of cells */
	cellPadding?: CellPadding;
	/** Direction in which the column is sorted */
	sorted?: "asc" | "desc";
	/** How should the content be aligned (defaults to "left") */
	align?: Alignment;
	/** Marks this column as key column (main content of the table) */
	isKeyColumn?: boolean;
	/** Defines the number of columns a table cell should span */
	colSpan?: number;
	/** Function to override options for a cell */
	overrideOptionsFor?: (
		item: T
	) => Pick<BaseTableColumnProps<T>, "width" | "align" | "cellPadding" | "colSpan">;
	/** Function to not render a cell */
	shouldRenderCell?: (item: T) => TrueFalsy;
	/** Custom className that will be applied to both the th and td elements in that column */
	className?: string;
	/** Column gets hidden in static mode */
	hideInStaticMode?: boolean;
	/** Displays an extra row after the column header with the summary of that column */
	summary?: SummaryConfig;
	/** Function to generate data-observe-keys for table cells */
	dataObserveKeys?: (item: T, pos: BaseTableCellPosition) => string | undefined;
}

export type Alignment = "left" | "center" | "right";

export type CellPadding = "small" | "normal" | "none";

export interface BaseTableCellPosition {
	columnNum: number;
	rowNum: number;
}

export function BaseTable<T>(props: BaseTableProps<T>): JSX.Element {
	const {
		columns: baseColumns,
		items,
		onExpandClick,
		expandedContentRenderer,
		expandedCellPosition,
		footer,
		topAligned,
		highlightRow,
		rowKey,
		withoutKeyColumn,
		thClassName,
		trClassName,
		tdClassName,
		className,
		style,
		caption,
		loading,
		loadingText,
		noDataState,
	} = props;
	const id = useUniqueId("table");
	const i18nLib = useTranslations();
	const i18nLab = useLabTranslations();
	const colId = (colNum: number) => `${id}-col-${colNum}`;
	const rowId = (rowNum: number) => `${id}-row-${rowNum}`;

	const isStatic = useStaticMode();
	const columns = isStatic ? baseColumns.filter((col) => !col.hideInStaticMode) : baseColumns;
	const keyColumnIdx = columns.findIndex((c) => c.isKeyColumn);
	if (keyColumnIdx === -1 && !withoutKeyColumn) {
		logError(
			"The table is missing a key column, either mark one of the columns as key column or set the withoutKeyColumn prop to true."
		);
	}
	const hasSummaryRow = columns.some((col) => col.summary !== undefined);

	return (
		<div className={cn(className, scss.tableContainer)} style={style}>
			<table
				data-component={props["data-component"] || "base-table"}
				data-observe-key={props["data-observe-key"]}
				className={cn(scss.table, "fancy-BaseTable")}
			>
				{caption && (
					<caption>
						<SrOnly>{caption}</SrOnly>
					</caption>
				)}
				<thead className={thClassName}>
					<tr>
						{columns.map((columnProp, columnNum) => {
							return (
								<th
									id={colId(columnNum)}
									scope="col"
									aria-sort={
										columnProp.sorted
											? columnProp.sorted === "asc"
												? "ascending"
												: "descending"
											: "none"
									}
									className={cn(
										scss.th,
										columnProp.className,
										scss[columnProp.align || "left"],
										columnProp.sorted && scss.highlight,
										"fancy-BaseTable-th"
									)}
									style={{ width: columnProp.width }}
									key={columnNum}
								>
									{columnProp.header}
								</th>
							);
						})}
					</tr>
					{hasSummaryRow && (
						<tr>
							{columns.map((columnProp, columnNum) => (
								<th
									scope="col"
									className={cn(
										scss.th,
										scss.summary,
										scss[columnProp.align || "left"],
										"fancy-BaseTable-th"
									)}
									style={{ width: columnProp.width }}
									key={columnNum}
								>
									{columnProp.summary && (
										<BigSmall
											className={scss.summaryContent}
											big={columnProp.summary.value}
											small={columnProp.summary.label}
										/>
									)}
								</th>
							))}
						</tr>
					)}
				</thead>
				<tbody>
					{items.length > 0 &&
						items.map((item, rowNum) => {
							let expandedCol;
							let isExpanded = false;
							const shouldHighlightRow = highlightRow && highlightRow(item);
							const row = (
								<tr
									className={cn(trClassName, shouldHighlightRow && scss.highlightRow)}
									aria-current={shouldHighlightRow ? "true" : undefined}
									key={(rowKey && rowKey(item)) ?? rowNum}
								>
									{columns.map((options, columnNum) => {
										// No need to do the rest of the logic, if cell shouldn't be rendered
										if (item && options.shouldRenderCell && !options.shouldRenderCell(item)) {
											return undefined;
										}

										if (options.overrideOptionsFor) {
											options = {
												...options,
												...options.overrideOptionsFor(item),
											};
										}

										const {
											align = "left",
											isExpandable,
											render,
											cellPadding = "normal",
											colSpan,
										} = options;

										if (eq(expandedCellPosition, { columnNum, rowNum })) {
											expandedCol = columnNum;
											isExpanded = true;
										} else {
											isExpanded = false;
										}

										const expandable =
											!isStatic &&
											isExpandable !== undefined &&
											item !== undefined &&
											isExpandable(item, { columnNum, rowNum });

										const attributes = {
											className: cn(
												tdClassName,
												options.className,
												scss.td,
												expandable ? scss.none : scss[cellPadding],
												topAligned && scss.topAligned,
												"fancy-BaseTable-td"
											),
											key: columnNum,
											colSpan,
										};

										const rendered = render(item, { columnNum, rowNum });

										const element = expandable ? (
											<TableExpander
												expanded={isExpanded}
												controls={rowId(rowNum)}
												onClick={(event) =>
													onExpandClick && onExpandClick(event, { columnNum, rowNum })
												}
												align={align}
												data-observe-key={options.dataObserveKeys?.(item, { columnNum, rowNum })}
											>
												{rendered}
											</TableExpander>
										) : (
											<Content
												padding="none"
												justifyContent={alignToFlex(align)}
												alignItems="center"
												data-observe-key={options.dataObserveKeys?.(item, { columnNum, rowNum })}
											>
												{rendered}
											</Content>
										);
										return keyColumnIdx === columnNum ? (
											<th scope="row" {...attributes}>
												{element}
											</th>
										) : (
											<td {...attributes}>{element}</td>
										);
									})}
								</tr>
							);
							const key = `${rowNum}-expanded`;
							return expandedCol !== undefined
								? [
										row,
										<tr key={key} id={rowId(rowNum)} className={scss.expandedContent}>
											<td
												className={`${scss.td} ${scss.nestedTable}`}
												colSpan={columns.length}
												headers={colId(expandedCol)}
											>
												{expandedContentRenderer &&
													expandedContentRenderer(item, expandedCellPosition!)}
											</td>
										</tr>,
								  ]
								: [row];
						})}
					{items.length === 0 && (
						<tr>
							<td
								className={cn(scss.td, loading && scss.loading, "fancy-BaseTable-td-states")}
								colSpan={columns.length}
							>
								{loading ? (
									<>
										<Spinner variant="dark" size="large" />
										<Paragraph className={cn(scss.loadingText)}>
											{loadingText ?? i18nLib.loading}
										</Paragraph>
									</>
								) : (
									noDataState ?? (
										<EmptyState type="reassure" heading={i18nLab.tableNoDataStateLabel} />
									)
								)}
							</td>
						</tr>
					)}
				</tbody>
				{footer && (
					<tfoot>
						<tr>
							<td className={scss.footer} colSpan={columns.length}>
								{footer}
							</td>
						</tr>
					</tfoot>
				)}
			</table>
		</div>
	);
}

function alignToFlex(align: Alignment): FlexAlignment {
	switch (align) {
		case "left":
			return "flex-start";
		case "center":
			return "center";
		case "right":
			return "flex-end";
	}
}

export interface ColumnConfigOptions<TDto> {
	align?: Alignment;
	cellEmptyState?: React.ReactNode | DtoToCellNode<TDto>;
	cellPadding?: CellPadding;
	width?: number;
	isKeyColumn?: boolean;
	weight?: number;
	colSpan?: number;
	className?: string;
	hideInStaticMode?: boolean;
	dataObserveKeys?: (item: TDto, pos: BaseTableCellPosition) => string | undefined;
}

export type DtoToCellNode<TDto> = (
	item: TDto,
	cellPosition: BaseTableCellPosition
) => React.ReactNode;

// Only picking options that make sense on a per row basis.
export type RowOptions<TDto> = Pick<
	ColumnConfigOptions<TDto>,
	"width" | "align" | "cellPadding" | "colSpan"
>;

export type DtoToExpandedNode<TDto> = (item: TDto) => React.ReactNode;

export interface ColumnExpandOptions<TDto> {
	cellExpandRenderer: DtoToExpandedNode<TDto>;
	canCellExpand: (item: TDto, cellPosition: BaseTableCellPosition) => TrueFalsy;
}

export interface ColumnConfig<TDto> {
	render: DtoToCellNode<TDto>;
	header: HeaderConfig<TDto> | SortableHeaderConfig<TDto>[] | BasicHeaderConfig;
	shouldRenderCell?: (dto: TDto) => TrueFalsy;
	options?: ColumnConfigOptions<TDto>;
	overrideOptionsFor?: (dto: TDto) => RowOptions<TDto>;
	expandOptions?: ColumnExpandOptions<TDto>;
	summary?: SummaryConfig;
	csv?: CsvColumnConfig<TDto> | CsvColumnConfig<TDto>[] | null;
}

export interface BasicHeaderConfig {
	contentNode: React.ReactNode;
}

export type HeaderConfigTooltip =
	| string
	| {
			content: string;
			icon?: React.ReactElement<SVGElement> | React.ReactElement<HTMLImageElement> | null;
	  };

export interface HeaderConfig<TDto> {
	property?: Extract<keyof TDto, string>;
	content?: string;
	tooltip?: HeaderConfigTooltip;
	defaultSortDirection?: "asc" | "desc";
	"data-observe-key"?: string;
	notSortable?: boolean;
}

export interface SortableHeaderConfig<TDto> {
	property: Extract<keyof TDto, string>;
	content: string;
	tooltip?: HeaderConfigTooltip;
	defaultSortDirection?: "asc" | "desc";
	"data-observe-key"?: string;
	notSortable?: boolean;
}

export interface SummaryConfig {
	value: string | number;
	label: string;
}

export type ColumnsArray<TDto> = (ColumnConfig<TDto> | null | false)[];
